import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { CheckSquare, ScanText, Search, Sparkles, Trash2, Upload, Wand2, X } from "lucide-react";
import VaultListRow from "./VaultListRow";

const ROW_H = 68;
const OVERSCAN = 8;

const VaultList = ({
  items,
  loading,
  loadingMore,
  total,
  search,
  onSearchChange,
  filtersActive,
  onClearFilters,
  activeId,
  selected,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onExtractSelected,
  onOcrSelected,
  onGenerateBrandedSelected,
  onDeleteSelected,
  onOpen,
  onLoadMore,
  onFiles,
  uploading,
  scrollRef,
}) => {
  const [range, setRange] = useState({ start: 0, end: 24 });
  const selectionActive = selected.length > 0;
  const measureRef = useRef(null);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const first = Math.floor(el.scrollTop / ROW_H);
    const visible = Math.ceil(el.clientHeight / ROW_H);

    setRange({ start: Math.max(0, first - OVERSCAN), end: first + visible + OVERSCAN });

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - ROW_H * 6) onLoadMore();
  }, [onLoadMore, scrollRef]);

  measureRef.current = measure;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const run = () => measureRef.current();
    run();
    el.addEventListener("scroll", run, { passive: true });

    const observer = new ResizeObserver(run);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", run);
      observer.disconnect();
    };
  }, [scrollRef, loading]);

  const visible = items.slice(range.start, Math.min(range.end, items.length));
  const padTop = range.start * ROW_H;
  const padBottom = Math.max(0, items.length - Math.min(range.end, items.length)) * ROW_H;
  const allSelected = items.length > 0 && items.every((row) => selected.includes(row.id));
  const ocrEligibleIds = items.filter((row) => selected.includes(row.id) && row.ocr_retryable).map((row) => row.id);

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 360 },
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        borderRight: "1px solid hsl(140 8% 88%)",
        backgroundColor: "#ffffff",
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25, flexShrink: 0, borderBottom: "1px solid hsl(140 8% 88%)" }}>
        {selectionActive ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: 38 }}>
            <Typography
              sx={{
                flex: 1,
                fontSize: 13,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
              }}
            >
              {selected.length} selected
            </Typography>

            <Tooltip arrow placement="top" title="Extract selected">
              <Button
                size="small"
                disableRipple
                onClick={onExtractSelected}
                aria-label="Extract selected"
                sx={{
                  minWidth: 0,
                  px: 1,
                  borderRadius: "6px",
                  color: "hsl(80 97% 72%)",
                  backgroundColor: "hsl(150 38% 17%)",
                  "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
                }}
              >
                <Wand2 size={14} />
              </Button>
            </Tooltip>

            <Tooltip
              arrow
              placement="top"
              title={ocrEligibleIds.length > 0 ? "Read the selected scans" : "None of the selected CVs need scanning, text already exists"}
            >
              <span>
                <Button
                  size="small"
                  disableRipple
                  disabled={ocrEligibleIds.length === 0}
                  onClick={() => onOcrSelected(ocrEligibleIds)}
                  aria-label="Read the selected scans"
                  sx={{
                    minWidth: 0,
                    px: 1,
                    borderRadius: "6px",
                    color: "hsl(150 30% 12%)",
                    border: "1px solid hsl(140 8% 88%)",
                    "&:hover": { backgroundColor: "hsl(140 10% 96%)" },
                    "&:disabled": { opacity: 0.4 },
                  }}
                >
                  <ScanText size={14} />
                </Button>
              </span>
            </Tooltip>

            <Tooltip arrow placement="top" title="Generate Squarehub CVs">
              <Button
                size="small"
                disableRipple
                onClick={onGenerateBrandedSelected}
                aria-label="Generate Squarehub CVs"
                sx={{
                  minWidth: 0,
                  px: 1,
                  borderRadius: "6px",
                  color: "hsl(150 30% 12%)",
                  border: "1px solid hsl(140 8% 88%)",
                  "&:hover": { backgroundColor: "hsl(140 10% 96%)" },
                }}
              >
                <Sparkles size={14} />
              </Button>
            </Tooltip>

            <Tooltip arrow placement="top" title="Delete selected">
              <Button
                size="small"
                disableRipple
                onClick={onDeleteSelected}
                aria-label="Delete selected"
                sx={{ minWidth: 0, px: 1, borderRadius: "6px", color: "hsl(150 10% 46%)", "&:hover": { color: "hsl(0 72% 51%)" } }}
              >
                <Trash2 size={14} />
              </Button>
            </Tooltip>

            <Button
              size="small"
              disableRipple
              onClick={onClearSelection}
              sx={{ minWidth: 0, px: 1, borderRadius: "6px", textTransform: "none", fontSize: 12.5, color: "hsl(150 10% 46%)" }}
            >
              Clear
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TextField
              size="small"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search name, email, phone, CV text…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={14} color="hsl(150 10% 46%)" />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  height: 38,
                  fontSize: 13,
                  borderRadius: "10px",
                  backgroundColor: "hsl(140 10% 97%)",
                  "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                  "&.Mui-focused": { backgroundColor: "#ffffff" },
                  "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 1 },
                },
              }}
            />

            <Tooltip arrow placement="top" title="Select all loaded">
              <Box component="span">
                <Button
                  size="small"
                  disableRipple
                  disabled={items.length === 0}
                  onClick={() => onSelectAll(!allSelected)}
                  aria-label="Select all loaded"
                  sx={{ minWidth: 0, px: 1, borderRadius: "6px", color: "hsl(150 10% 46%)", "&:hover": { color: "hsl(150 38% 17%)" } }}
                >
                  <CheckSquare size={15} />
                </Button>
              </Box>
            </Tooltip>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, minHeight: 16 }}>
          <Typography sx={{ fontSize: 11, color: "hsl(150 10% 55%)" }}>
            {loading ? "Loading…" : `${items.length} of ${total}`}
          </Typography>
          {filtersActive && (
            <Button
              size="small"
              disableRipple
              startIcon={<X size={11} />}
              onClick={onClearFilters}
              sx={{
                textTransform: "none",
                fontSize: 11,
                py: 0,
                minWidth: 0,
                borderRadius: "6px",
                color: "hsl(150 10% 46%)",
                "& .MuiButton-startIcon": { mr: 0.25 },
              }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={22} sx={{ color: "hsl(150 38% 17%)" }} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: "center", px: 3, py: 8 }}>
            <Upload size={22} color="hsl(150 10% 60%)" />
            <Typography
              sx={{ fontSize: 13, mt: 1.25, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}
            >
              {filtersActive ? "Nothing matches" : "No CVs yet"}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "hsl(150 10% 55%)", mt: 0.5 }}>
              {filtersActive ? "Try another search or clear the filters." : "Drop PDF or DOCX files anywhere on this page."}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ height: padTop }} />
            {visible.map((item) => (
              <VaultListRow
                key={item.id}
                item={item}
                height={ROW_H}
                active={activeId === item.id}
                selected={selected.includes(item.id)}
                showCheck={selectionActive}
                onToggleSelect={onToggleSelect}
                onOpen={onOpen}
              />
            ))}
            <Box sx={{ height: padBottom }} />
            {loadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={16} sx={{ color: "hsl(150 38% 17%)" }} />
              </Box>
            )}
          </>
        )}
      </Box>

      <Box
        component="label"
        sx={{
          flexShrink: 0,
          m: 1,
          height: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          cursor: "pointer",
          borderRadius: "0.75rem",
          border: "1px dashed hsl(140 8% 78%)",
          backgroundColor: "hsl(140 10% 97%)",
          transition: "border-color 140ms ease, background-color 140ms ease",
          "&:hover": { borderColor: "hsl(150 38% 17%)", backgroundColor: "hsl(140 10% 95%)" },
        }}
      >
        {uploading > 0 ? (
          <>
            <CircularProgress size={13} sx={{ color: "hsl(150 38% 17%)" }} />
            <Typography sx={{ fontSize: 12, color: "hsl(150 30% 12%)" }}>
              Uploading {uploading} file{uploading === 1 ? "" : "s"}…
            </Typography>
          </>
        ) : (
          <>
            <Upload size={14} color="hsl(150 10% 46%)" />
            <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>Drop CVs here, or click to add</Typography>
          </>
        )}
        <input hidden multiple type="file" accept=".pdf,.docx" onChange={(e) => onFiles(e.target.files)} />
      </Box>
    </Box>
  );
};

export default VaultList;
